import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useNavigate } from "react-router";

import { NotFoundPage } from "design-system/components/NotFoundPage";
import { SandboxAuthProvider } from "sandbox/contexts/SandboxAuthContext";
import { ThemeModeProvider } from "sandbox/contexts/ThemeModeContext";

import {
  AuthScreen,
  Credentials,
  Explorer,
  ForgotPasswordScreen,
  ResetPasswordScreen,
  TermsOfUseScreen,
  VerifyEmailConfirmScreen,
} from "./features";
import { AuthCardLayout } from "./features/layouts/AuthCardLayout";
import { SandboxLayout } from "./features/layouts/SandboxLayout";
import { sandboxPaths as paths } from "./paths";

const SandboxThemeLayout = () => (
  <ThemeModeProvider>
    <Outlet />
  </ThemeModeProvider>
);

const SandboxAuthLayout = () => (
  <SandboxAuthProvider>
    <Outlet />
  </SandboxAuthProvider>
);

const AuthCardRoute = ({ wide }: { wide?: boolean }) => (
  <AuthCardLayout wide={wide}>
    <Outlet />
  </AuthCardLayout>
);

const SandboxNotFoundRoute = () => {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();

  return (
    <NotFoundPage
      title={t("common:components.notFound.title")}
      description={t("common:components.notFound.description")}
      homeButtonLabel={t("common:components.notFound.backToHome")}
      onNavigateHome={() => navigate(paths.index)}
    />
  );
};

export const sandboxRoutes = [
  {
    element: <SandboxThemeLayout />,
    children: [
      {
        element: <SandboxAuthLayout />,
        children: [
          {
            path: paths.index,
            element: <SandboxLayout />,
            children: [
              {
                index: true,
                element: <Navigate to={paths.explorer} replace />,
              },
              { path: paths.explorer, element: <Explorer /> },
              { path: paths.credentials, element: <Credentials /> },
              { path: "*", element: <SandboxNotFoundRoute /> },
            ],
          },
          {
            path: paths.login,
            element: <AuthScreen />,
          },
          {
            path: paths.terms,
            element: <TermsOfUseScreen />,
          },
          {
            path: paths.forgotPassword,
            element: <AuthCardRoute wide />,
            children: [{ index: true, element: <ForgotPasswordScreen /> }],
          },
          {
            path: paths.resetPassword,
            element: <AuthCardRoute />,
            children: [{ index: true, element: <ResetPasswordScreen /> }],
          },
        ],
      },
      {
        path: paths.verifyEmail,
        element: <AuthCardRoute />,
        children: [{ index: true, element: <VerifyEmailConfirmScreen /> }],
      },
    ],
  },
];
